// DRAFT — not legal advice. Every factual assertion here must be confirmed by LAA and reviewed by counsel before this site accepts public traffic or runs paid advertising.
import { createFileRoute, Link } from "@tanstack/react-router";

import { seo } from "@/lib/seo";
import { NAP, SHOW_PHONE } from "@/lib/business";
import { LegalPage, LegalSection } from "@/components/legal/LegalPage";

export const Route = createFileRoute("/_site/legal/terms")({
  head: () =>
    seo({
      title: "Terms of Service",
      description:
        "The terms that govern use of the LAA Concierge Consulting website and how consulting engagements begin, run, and end.",
      path: "/legal/terms",
    }),
  component: Page,
});

function Page() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Service"
      lastUpdated="4 September 2026"
      intro="These terms govern your use of this website and set out the ground rules for working with us. Read them before you engage us."
    >
      <LegalSection title="Who we are">
        <p>
          {NAP.legalName} is an independent business consulting firm registered in Nevada, with
          offices at {NAP.street}, {NAP.city}, {NAP.region} {NAP.postalCode}. We help businesses
          identify where artificial intelligence produces measurable value in their operations and
          then build and deploy those systems. We also advise on business funding, including SBA
          loan preparation, grant applications, and business plan writing.
        </p>
        <p>
          This website exists to describe that work and to let you start a conversation. It is
          general information about our services, not advice about your situation.
        </p>
      </LegalSection>

      <LegalSection title="Using the site does not create a relationship">
        <p>
          Reading this site, downloading anything from it, using the AI Opportunity Scorecard,
          submitting the contact form, requesting a call, or speaking with us on an introductory
          call{" "}
          <strong>
            does not create a consulting, advisory, fiduciary, or professional relationship
          </strong>{" "}
          between you and LAA. Nothing you send us through this site is treated as confidential
          until a written scope agreement is in place — so do not send us sensitive material before
          then.
        </p>
        <p>
          The scorecard and any other tool on this site produces an indicative result from the
          answers you give. It is a conversation starter, not a diagnosis of your business, and it
          does not account for facts we have not been told.
        </p>
      </LegalSection>

      <LegalSection title="How an engagement actually begins">
        <p>
          We do not start work on a handshake. Every engagement begins with a written scope
          agreement, signed by both parties before any work is performed, which sets out at minimum:
        </p>
        <ul>
          <li>the specific work to be done and what will be delivered;</li>
          <li>what is expressly out of scope;</li>
          <li>the fee, the payment schedule, and any expenses;</li>
          <li>the timeline and what we need from you to meet it;</li>
          <li>who at your organisation makes decisions and approves deliverables.</li>
        </ul>
        <p>
          Where the scope agreement and these terms conflict, the scope agreement controls for that
          engagement. Changes to scope are agreed in writing before the additional work starts;
          otherwise we will tell you what is out of scope and quote it separately rather than doing
          it and billing you afterwards.
        </p>
      </LegalSection>

      <LegalSection title="Fees and payment">
        <p>
          Our published rates are set out in your scope agreement and are indicative starting
          points; the fee for your engagement is the one stated in your scope agreement. Fixed-fee
          work is typically invoiced with a deposit before work begins and the balance on delivery.
          Ongoing work is invoiced monthly in arrears. Paid working sessions are payable in advance.
        </p>
        <p>
          Invoices are due within 15 days of the invoice date unless the scope agreement says
          otherwise. Overdue amounts may accrue interest at the lower of 1.5 percent per month or
          the maximum rate permitted by Nevada law, and we may suspend work on written notice while
          an invoice is materially overdue. Fees are exclusive of applicable taxes and of
          third-party costs such as software licences and platform usage charges, which are yours
          unless we have agreed otherwise in writing. Fees are for advisory and preparation services
          only and are not contingent on any outcome.
        </p>
      </LegalSection>

      <LegalSection title="Ownership of what we produce">
        <p>
          On full payment of the fees for the engagement, you own the deliverables created
          specifically for you under that engagement: the written analysis, the opportunity map, the
          configuration and custom code we build for your systems, and the documentation that goes
          with them.
        </p>
        <p>
          LAA retains ownership of everything we brought to the engagement or developed
          independently of it — our methods, frameworks, assessment templates, scoring models,
          reusable code libraries, and general know-how, including skills and experience gained
          while doing your work. We may reuse all of that for other clients. Nothing in this section
          gives us the right to reuse your confidential information or anything identifiable to your
          business.
        </p>
        <p>
          Deliverables may incorporate third-party software and services under their own licences.
          Those licences govern that software; we will identify material ones in the deliverable.
        </p>
      </LegalSection>

      <LegalSection title="Confidentiality">
        <p>
          Each party will keep the other's confidential information in confidence, use it only for
          the engagement, disclose it only to people who need it and are under equivalent
          obligations, and protect it with at least reasonable care. This applies in both
          directions: your operational and financial information, and our non-public methods,
          pricing, and materials.
        </p>
        <p>
          These obligations do not apply to information that is public through no breach, was
          already known without a duty of confidence, is independently developed, or is lawfully
          received from a third party. Where disclosure is legally compelled, the disclosing party
          will give prompt notice where lawful so the other can seek protection. Obligations survive
          the end of the engagement for three years, and indefinitely for anything that is a trade
          secret. We will not name you as a client or describe your engagement publicly without your
          written permission.
        </p>
      </LegalSection>

      <LegalSection title="AI-assisted work and your responsibility for decisions">
        <p>
          Our work involves artificial intelligence — both as the subject of the engagement and as a
          tool we use while doing it. AI systems generate probabilistic output. They can be
          confidently wrong, they reflect limitations in their training data, and their behaviour
          changes as underlying models change.
        </p>
        <p>
          A consultant reviews AI-assisted output before it reaches you. That review reduces error;
          it does not eliminate it. You remain responsible for the business decisions you make,
          including decisions informed by our deliverables, and for the operation of any system we
          build once it is handed over. Where a system's output affects customers, employees, money,
          or anything a regulator cares about, keep a human in the loop and validate the output
          against your own records before acting on it. We will say so in the deliverable when a
          system needs that supervision, and we recommend it as the default.
        </p>
      </LegalSection>

      <LegalSection title="No guarantee of outcome">
        <p>
          We do not guarantee any particular result. That includes revenue increases, cost savings,
          efficiency gains, productivity improvements, loan approval, grant awards, or any other
          funding or business outcome. Results depend on facts outside our control: your data, your
          team's adoption, your market, and decisions made by lenders, agencies, and other third
          parties.
        </p>
        <p>
          Any figures, timelines, ranges, or comparisons shown on this site or in our materials are
          illustrative unless we expressly state a documented basis for them. Services are provided
          with reasonable skill and care; beyond that, and to the extent permitted by law, they are
          provided without warranties of any kind, express or implied, including implied warranties
          of merchantability, fitness for a particular purpose, and non-infringement.
        </p>
      </LegalSection>

      <LegalSection title="Limitation of liability">
        <p>
          To the fullest extent permitted by law, neither party is liable to the other for indirect,
          incidental, special, consequential, exemplary, or punitive damages, or for lost profits,
          lost revenue, lost data, or business interruption, even if advised that such damages were
          possible.
        </p>
        <p>
          To the fullest extent permitted by law, LAA's total aggregate liability arising out of or
          relating to an engagement, whether in contract, tort, or otherwise, is limited to the
          total fees you actually paid us under that engagement in the 12 months preceding the event
          giving rise to the claim. For use of this website where there is no engagement, our
          liability is limited to one hundred US dollars.
        </p>
        <p>
          Nothing in these terms excludes or limits liability that cannot lawfully be excluded or
          limited, including liability for fraud or for death or personal injury caused by
          negligence.
        </p>
      </LegalSection>

      <LegalSection title="Indemnity">
        <p>
          You will indemnify, defend, and hold harmless LAA and its members, officers, employees,
          and contractors from third-party claims, losses, damages, liabilities, and reasonable
          legal costs arising out of your use of a deliverable in a way not contemplated by the
          scope agreement, your operation or modification of a delivered system after handover, your
          breach of these terms or of applicable law, or content and data you supplied to us where
          you did not have the right to supply it. We will notify you promptly of any such claim,
          give you control of the defence, and cooperate at your expense.
        </p>
      </LegalSection>

      <LegalSection title="Your use of this site">
        <p>
          You may read, print, and share this site's pages for your own business purposes. You may
          not scrape it at a volume that burdens the service, submit false information through our
          forms, use the forms to send unsolicited commercial messages, attempt to gain unauthorised
          access to any system behind it, or copy substantial parts of its content for a competing
          commercial offering. Site content, branding, and design are owned by LAA or our licensors.
        </p>
        <p>
          The site may link to third-party websites. We do not control them, and a link is not an
          endorsement. Your use of a linked site is governed by that site's terms.
        </p>
      </LegalSection>

      <LegalSection title="Governing law and venue">
        <p>
          These terms and any engagement are governed by the laws of the State of Nevada, without
          regard to its conflict-of-laws rules. The exclusive venue for any dispute is the state or
          federal courts located in Clark County, Nevada, and both parties consent to personal
          jurisdiction there. Before filing, the parties will attempt to resolve the dispute in good
          faith for 30 days after written notice.
        </p>
        <p>
          If any provision is held unenforceable, it is limited or severed to the minimum extent
          necessary and the rest remains in force. A failure to enforce a provision is not a waiver
          of it.
        </p>
      </LegalSection>

      <LegalSection title="Changes to these terms">
        <p>
          We may update these terms. The "Last updated" date at the top of this page shows when we
          last did. Changes apply to site use from the date they are posted, and to engagements
          entered into after that date; they do not change the terms of a scope agreement already
          signed unless both parties agree in writing.
        </p>
        <p>
          Questions go to <a href={`mailto:${NAP.email}`}>{NAP.email}</a>
          {SHOW_PHONE ? (
            <>
              {" or "}
              <a href={NAP.phoneHref}>{NAP.phone}</a>
            </>
          ) : null}
          . See also our <Link to="/legal/privacy">Privacy Policy</Link> and{" "}
          <Link to="/legal/disclaimer">Disclaimer</Link>.
        </p>
      </LegalSection>
    </LegalPage>
  );
}
